// Modelo de "stacked metric rows" del modal de Configuración de columnas.
// Cada fila representa UNA métrica y agrupa todas sus variantes de período como chips clickeables.
// Esto colapsa la "explosión combinatoria" (CPA × 7 períodos = 7 filas → 1 fila con 7 chips).
//
// Dos tipos de filas:
//   1. FIXED — chips apuntan a columnIds predefinidos (TODAY_CPA_COLUMN, CURRENT_PERIOD_CPA_COLUMN, etc.)
//   2. STANDALONE — fila simple sin chips (Saldo, Etiquetas, Comentarios, etc.)
//
// Las flex indicators dinámicas (configuradas por la agencia) se inyectan como filas FIXED
// adicionales armadas a partir del catálogo props.flexIndicators.

import {
    TODAY_CLICKS_COLUMN, TODAY_IMPRESSIONS_COLUMN, TODAY_COST_COLUMN, TODAY_CONVERSIONS_COLUMN,
    TODAY_CPC_COLUMN, TODAY_CPA_COLUMN, TODAY_CTR_COLUMN, TODAY_CPM_COLUMN,
    CURRENT_PERIOD_CPA_COLUMN,
    CURRENT_PERIOD_CPA_VS_LAST_PERIOD_CPA_COLUMN, CURRENT_PERIOD_CPA_VS_LAST_THREE_PERIODS_CPA_COLUMN,
    CURRENT_PERIOD_CPA_VS_LAST_SIX_PERIODS_CPA_COLUMN,
    YESTERDAY_CPA_VS_LAST_PERIOD_COLUMN, YESTERDAY_CPA_VS_LAST_THREE_PERIODS_CPA_COLUMN,
    YESTERDAY_CPA_VS_LAST_SIX_PERIODS_CPA_COLUMN,
    FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN, FULL_PERIOD_COST_VS_PROJECTED_COLUMN, FULL_PERIOD_ODS_COLUMN, YESTERDAY_EXPENSES_COLUMN,
    LAST_7_DAYS_AVERAGE_EXPENSES_COLUMN, FULL_PERIOD_ODS_VS_YESTERDAY_COLUMN,
    FULL_PERIOD_ODS_VS_LAST_7_DAYS_COLUMN, FULL_PERIOD_ODS_VS_PLATFORM_BUDGET_COLUMN, FULL_PERIOD_CONVERSIONS_COLUMN,
    PURCHASES_VALUE_COLUMN, LAST_PERIOD_PURCHASES_VALUE_COLUMN, ROAS_COLUMN, ACOS_COLUMN,
    FULL_PERIOD_ROAS_VS_LAST_MONTH_COLUMN, FULL_PERIOD_ACOS_VS_LAST_MONTH_COLUMN,
    CURRENT_PERIOD_ALLOCATED_BUDGET_COLUMN, CURRENT_PERIOD_PERIOD_DATES_COLUMN,
    CURRENT_PERIOD_ROLLOVER_BALANCE_COLUMN, CURRENT_PERIOD_TOTAL_BUDGET_COLUMN,
    CURRENT_PERIOD_COST_COLUMN,
    LAST_PERIOD_ALLOCATED_BUDGET_COLUMN,
    LAST_PERIOD_COST_COLUMN,
    LABELS_COLUMN, CURRENCY_COLUMN,
    GOAL_1_COLUMN, GOAL_1_INFO_COLUMN, GOAL_2_COLUMN, GOAL_2_INFO_COLUMN,
    AVAILABLE_FUNDS_COLUMN, DAYS_TILL_FUNDS_ARE_OUT_COLUMN, AVAILABLE_FUNDS_LAST_UPLOAD_ON_COLUMN,
    COMMENTS_COLUMN, PLATFORM_BUDGET_COLUMN, CAMPAIGN_STATUS_COLUMN,
    CURRENT_PERIOD_TIME_COMPLETION_COLUMN, CURRENT_PERIOD_AMOUNT_COMPLETION_COLUMN,
    CURRENT_PERIOD_COST_VS_TIME_COLUMN,
    AGENCY_FLEX_INDICATOR, getPeriodsData, FLEX_INDICATOR_COLUMN_TO_DATA, getMetricName,
    buildAutoCustomMetricIndicators,
} from "./columns";
import {compactPeriodLabel} from "./columns_compact_labels";
import {
    CATEGORY_GOALS, CATEGORY_BUDGET, CATEGORY_PERFORMANCE, CATEGORY_CONVERSIONS,
    CATEGORY_FUNDS, CATEGORY_ATTRIBUTES, CATEGORY_STATUS, CATEGORY_CUSTOM,
    CHANNEL_PAID_MEDIA, CHANNEL_GA4, CHANNEL_ECOMMERCE, CHANNEL_BU,
    categorizeFlexMetric,
} from "./columns_metadata";

export const ROW_TYPE_FIXED = 'FIXED';
export const ROW_TYPE_STANDALONE = 'STANDALONE';

// Construye el catálogo completo de stacked rows para el contexto actual (BU + flex indicators).
// Devuelve filas agrupadas por categoría.
//
// IMPORTANTE: en Dashbo Backend hay 2 tipos de "flex indicators":
//   - `flexIndicators` → PERSONALIZADOS de la agencia (badge ✨ en UI). Editables en config de la agencia.
//   - `ga4Indicators`, `ecommerceIndicators`, `paidMediaIndicators`, `metaIndicators`, `googleIndicators`
//     → FIXED para todas las agencies. Misma estructura `FLEX_INDICATOR||metric||main||vs` pero NO custom.
export function buildStackedRows({t, flexIndicators, ga4Indicators, ecommerceIndicators, paidMediaIndicators, metaIndicators, googleIndicators, tiktokIndicators, customMetrics, customMetricIndicators, bu}) {
    const periods = getPeriodsData(t);
    const rows = [];

    // -------- GOALS --------
    rows.push({
        id: 'GOAL_1', titleKey: t('config_columns_modal.rows.goal_1', 'Objetivo 1°'),
        iconName: 'bullseye', category: CATEGORY_GOALS, channel: CHANNEL_BU,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: t('config_columns_modal.chips.config', 'Configuración'), columnId: GOAL_1_INFO_COLUMN},
            {label: t('config_columns_modal.chips.performance', 'Rendimiento'), columnId: GOAL_1_COLUMN},
        ],
    });
    rows.push({
        id: 'GOAL_2', titleKey: t('config_columns_modal.rows.goal_2', 'Objetivo 2°'),
        iconName: 'bullseye', category: CATEGORY_GOALS, channel: CHANNEL_BU,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: t('config_columns_modal.chips.config', 'Configuración'), columnId: GOAL_2_INFO_COLUMN},
            {label: t('config_columns_modal.chips.performance', 'Rendimiento'), columnId: GOAL_2_COLUMN},
        ],
    });

    // -------- BUDGET --------
    rows.push({
        id: 'BUDGET', titleKey: t('config_columns_modal.rows.budget', 'Presupuesto'),
        iconName: 'piggy-bank', category: CATEGORY_BUDGET, channel: CHANNEL_BU,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: periods.THIS_PERIOD, columnId: CURRENT_PERIOD_ALLOCATED_BUDGET_COLUMN},
            {label: t('config_columns_modal.chips.total_period', 'Total período'), columnId: CURRENT_PERIOD_TOTAL_BUDGET_COLUMN},
            {label: t('config_columns_modal.chips.rollover', 'Saldo arrastrado'), columnId: CURRENT_PERIOD_ROLLOVER_BALANCE_COLUMN},
            {label: periods.LAST_MONTH, columnId: LAST_PERIOD_ALLOCATED_BUDGET_COLUMN},
        ],
    });
    rows.push({
        id: 'PLATFORM_BUDGET', titleKey: t('config_columns_modal.rows.platform_budget', 'Presupuesto en plataforma'),
        iconName: 'sliders-h', category: CATEGORY_BUDGET, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_STANDALONE,
        chips: [{label: t('config_columns_modal.chips.daily_equiv', 'Equivalente diario'), columnId: PLATFORM_BUDGET_COLUMN}],
    });
    rows.push({
        id: 'PERIOD_PROGRESS', titleKey: t('config_columns_modal.rows.period_progress', 'Avance del período'),
        iconName: 'tachometer-alt', category: CATEGORY_BUDGET, channel: CHANNEL_BU,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: t('config_columns_modal.chips.time_completion', '% Tiempo'), columnId: CURRENT_PERIOD_TIME_COMPLETION_COLUMN},
            {label: t('config_columns_modal.chips.amount_completion', '% Invertido'), columnId: CURRENT_PERIOD_AMOUNT_COMPLETION_COLUMN},
            {label: t('config_columns_modal.chips.cost_vs_time', 'Invertido vs Tiempo'), columnId: CURRENT_PERIOD_COST_VS_TIME_COLUMN},
        ],
    });
    rows.push({
        id: 'IDEAL_PACE', titleKey: t('config_columns_modal.rows.ideal_pace', 'Ritmo ideal de inversión'),
        iconName: 'compass', category: CATEGORY_BUDGET, channel: CHANNEL_BU,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: t('config_columns_modal.chips.daily_ideal', 'Costo diario ideal'), columnId: FULL_PERIOD_ODS_COLUMN},
            {label: t('config_columns_modal.chips.real_vs_ideal', 'Desvío vs presupuesto'), columnId: FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN},
            {label: t('config_columns_modal.chips.cost_vs_projected', 'Desvío vs inv. esperada'), columnId: FULL_PERIOD_COST_VS_PROJECTED_COLUMN},
            {label: t('config_columns_modal.chips.ideal_vs_yesterday', 'Ideal vs Ayer'), columnId: FULL_PERIOD_ODS_VS_YESTERDAY_COLUMN},
            {label: t('config_columns_modal.chips.ideal_vs_7d', 'Ideal vs Prom. 7d'), columnId: FULL_PERIOD_ODS_VS_LAST_7_DAYS_COLUMN},
            {label: t('config_columns_modal.chips.ideal_vs_platform', 'Ideal vs Plataforma'), columnId: FULL_PERIOD_ODS_VS_PLATFORM_BUDGET_COLUMN},
        ],
    });

    // -------- PERFORMANCE (Paid Media core metrics) --------
    rows.push({
        id: 'COST', titleKey: t('config_columns_modal.rows.cost', 'Costo'),
        iconName: 'dollar-sign', category: CATEGORY_PERFORMANCE, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: periods.TODAY, columnId: TODAY_COST_COLUMN},
            {label: periods.YESTERDAY, columnId: YESTERDAY_EXPENSES_COLUMN},
            {label: periods.THIS_PERIOD, columnId: CURRENT_PERIOD_COST_COLUMN},
            {label: t('config_columns_modal.chips.last_7d_avg', 'Prom. últ. 7d'), columnId: LAST_7_DAYS_AVERAGE_EXPENSES_COLUMN},
            {label: periods.LAST_MONTH, columnId: LAST_PERIOD_COST_COLUMN},
        ],
    });
    rows.push({
        id: 'CLICKS', titleKey: t('config_columns_modal.rows.clicks', 'Clicks'),
        iconName: 'mouse-pointer', category: CATEGORY_PERFORMANCE, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [{label: periods.TODAY, columnId: TODAY_CLICKS_COLUMN}],
    });
    rows.push({
        id: 'IMPRESSIONS', titleKey: t('config_columns_modal.rows.impressions', 'Impresiones'),
        iconName: 'eye', category: CATEGORY_PERFORMANCE, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [{label: periods.TODAY, columnId: TODAY_IMPRESSIONS_COLUMN}],
    });
    rows.push({
        id: 'CPC', titleKey: 'CPC',
        iconName: 'mouse-pointer', category: CATEGORY_PERFORMANCE, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [{label: periods.TODAY, columnId: TODAY_CPC_COLUMN}],
    });
    rows.push({
        id: 'CTR', titleKey: 'CTR',
        iconName: 'percent', category: CATEGORY_PERFORMANCE, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [{label: periods.TODAY, columnId: TODAY_CTR_COLUMN}],
    });
    rows.push({
        id: 'CPM', titleKey: 'CPM',
        iconName: 'eye', category: CATEGORY_PERFORMANCE, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [{label: periods.TODAY, columnId: TODAY_CPM_COLUMN}],
    });

    // -------- CONVERSIONS --------
    rows.push({
        id: 'CONVERSIONS', titleKey: t('config_columns_modal.rows.conversions', 'Conversiones'),
        iconName: 'check-double', category: CATEGORY_CONVERSIONS, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: periods.TODAY, columnId: TODAY_CONVERSIONS_COLUMN},
            {label: periods.THIS_PERIOD, columnId: FULL_PERIOD_CONVERSIONS_COLUMN},
        ],
    });
    rows.push({
        id: 'CPA', titleKey: 'CPA',
        iconName: 'bullseye', category: CATEGORY_CONVERSIONS, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: periods.TODAY, columnId: TODAY_CPA_COLUMN},
            {label: periods.THIS_PERIOD, columnId: CURRENT_PERIOD_CPA_COLUMN},
            {label: t('config_columns_modal.chips.vs_last_month', 'vs Mes anterior'), columnId: CURRENT_PERIOD_CPA_VS_LAST_PERIOD_CPA_COLUMN},
            {label: t('config_columns_modal.chips.vs_last_3m', 'vs Últ. 3m'), columnId: CURRENT_PERIOD_CPA_VS_LAST_THREE_PERIODS_CPA_COLUMN},
            {label: t('config_columns_modal.chips.vs_last_6m', 'vs Últ. 6m'), columnId: CURRENT_PERIOD_CPA_VS_LAST_SIX_PERIODS_CPA_COLUMN},
            {label: t('config_columns_modal.chips.yesterday_vs_lp', 'Ayer vs Mes ant.'), columnId: YESTERDAY_CPA_VS_LAST_PERIOD_COLUMN},
            {label: t('config_columns_modal.chips.yesterday_vs_3m', 'Ayer vs Últ. 3m'), columnId: YESTERDAY_CPA_VS_LAST_THREE_PERIODS_CPA_COLUMN},
            {label: t('config_columns_modal.chips.yesterday_vs_6m', 'Ayer vs Últ. 6m'), columnId: YESTERDAY_CPA_VS_LAST_SIX_PERIODS_CPA_COLUMN},
        ],
    });
    rows.push({
        id: 'PURCHASES_VALUE', titleKey: t('config_columns_modal.rows.purchases_value', 'Valor de compras'),
        iconName: 'shopping-cart', category: CATEGORY_CONVERSIONS, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: periods.THIS_PERIOD, columnId: PURCHASES_VALUE_COLUMN},
            {label: periods.LAST_MONTH, columnId: LAST_PERIOD_PURCHASES_VALUE_COLUMN},
        ],
    });
    rows.push({
        id: 'ROAS', titleKey: 'ROAS',
        iconName: 'chart-bar', category: CATEGORY_CONVERSIONS, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: periods.THIS_PERIOD, columnId: ROAS_COLUMN},
            {label: t('config_columns_modal.chips.vs_last_month', 'vs Mes anterior'), columnId: FULL_PERIOD_ROAS_VS_LAST_MONTH_COLUMN},
        ],
    });
    rows.push({
        id: 'ACOS', titleKey: 'ACOS',
        iconName: 'chart-pie', category: CATEGORY_CONVERSIONS, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: periods.THIS_PERIOD, columnId: ACOS_COLUMN},
            {label: t('config_columns_modal.chips.vs_last_month', 'vs Mes anterior'), columnId: FULL_PERIOD_ACOS_VS_LAST_MONTH_COLUMN},
        ],
    });

    // -------- FUNDS --------
    rows.push({
        id: 'FUNDS', titleKey: t('config_columns_modal.rows.funds', 'Saldo'),
        iconName: 'wallet', category: CATEGORY_FUNDS, channel: CHANNEL_BU,
        type: ROW_TYPE_FIXED,
        chips: [
            {label: t('config_columns_modal.chips.balance', 'Saldo disponible'), columnId: AVAILABLE_FUNDS_COLUMN},
            {label: t('config_columns_modal.chips.days_left', 'Días restantes'), columnId: DAYS_TILL_FUNDS_ARE_OUT_COLUMN},
            {label: t('config_columns_modal.chips.last_upload', 'Última carga'), columnId: AVAILABLE_FUNDS_LAST_UPLOAD_ON_COLUMN},
        ],
    });

    // -------- ATTRIBUTES & STATUS --------
    rows.push({
        id: 'PERIOD_DATES', titleKey: t('config_columns_modal.rows.period_dates', 'Fechas del período'),
        iconName: 'calendar-alt', category: CATEGORY_ATTRIBUTES, channel: CHANNEL_BU,
        type: ROW_TYPE_STANDALONE,
        chips: [{label: t('config_columns_modal.chips.dates', 'Fechas'), columnId: CURRENT_PERIOD_PERIOD_DATES_COLUMN}],
    });
    rows.push({
        id: 'LABELS', titleKey: t('config_columns_modal.rows.labels', 'Etiquetas'),
        iconName: 'tags', category: CATEGORY_ATTRIBUTES, channel: CHANNEL_BU,
        type: ROW_TYPE_STANDALONE,
        chips: [{label: t('config_columns_modal.chips.labels', 'Etiquetas'), columnId: LABELS_COLUMN}],
    });
    rows.push({
        id: 'CURRENCY', titleKey: t('config_columns_modal.rows.currency', 'Moneda'),
        iconName: 'dollar-sign', category: CATEGORY_ATTRIBUTES, channel: CHANNEL_BU,
        type: ROW_TYPE_STANDALONE,
        chips: [{label: t('config_columns_modal.chips.currency', 'Moneda'), columnId: CURRENCY_COLUMN}],
    });
    rows.push({
        id: 'COMMENTS', titleKey: t('config_columns_modal.rows.comments', 'Comentarios'),
        iconName: 'comment-alt', category: CATEGORY_ATTRIBUTES, channel: CHANNEL_BU,
        type: ROW_TYPE_STANDALONE,
        chips: [{label: t('config_columns_modal.chips.comments', 'Comentarios'), columnId: COMMENTS_COLUMN}],
    });
    rows.push({
        id: 'CAMPAIGN_STATUS', titleKey: t('config_columns_modal.rows.campaign_status', 'Estado de campaña'),
        iconName: 'circle', category: CATEGORY_STATUS, channel: CHANNEL_PAID_MEDIA,
        type: ROW_TYPE_STANDALONE,
        chips: [{label: t('config_columns_modal.chips.status', 'Estado'), columnId: CAMPAIGN_STATUS_COLUMN}],
    });

    // -------- FLEX-FORMAT INDICATORS --------
    // Todos los arrays vienen como STRINGS (columnIds tipo "FLEX_INDICATOR||Cost||THIS_PERIOD||PREVIOUS_PERIOD").
    // Sólo `flexIndicators` son PERSONALIZADOS (badge ✨). El resto son fixed para todas las agencies.
    const customIds = new Set(flexIndicators || []);
    // Mapa CUSTOM:<id> → metadata de la métrica personalizada (para resolver display_name en flex).
    const customMetricsByKeyAll = {};
    (customMetrics || []).forEach((cm) => {
        if (cm && cm.id != null) customMetricsByKeyAll['CUSTOM:' + cm.id] = cm;
    });
    const allIds = [
        ...(flexIndicators || []),
        ...(ga4Indicators || []),
        ...(ecommerceIndicators || []),
        ...(paidMediaIndicators || []),
        ...(metaIndicators || []),
        ...(googleIndicators || []),
        ...(tiktokIndicators || []),
    ];
    const seenColumnIds = new Set();
    const flexRowsByMetric = {};
    allIds.forEach((columnId) => {
        if (typeof columnId !== 'string' || !columnId.includes(AGENCY_FLEX_INDICATOR)) return;
        if (seenColumnIds.has(columnId)) return;
        seenColumnIds.add(columnId);

        const parts = FLEX_INDICATOR_COLUMN_TO_DATA(columnId);
        if (!parts) return;
        const {metricName, mainPeriod, vsPeriod} = parts;
        if (!metricName) return;

        const compact = compactPeriodLabel(t, mainPeriod, vsPeriod);

        const lowerName = metricName.toLowerCase();
        const isGa4 = metricName.includes('GA4');
        const isEcommerce = lowerName.includes('ecommerce');
        const channel = isGa4 ? CHANNEL_GA4 : (isEcommerce ? CHANNEL_ECOMMERCE : CHANNEL_PAID_MEDIA);
        const category = categorizeFlexMetric(metricName);
        const requiresIntegration = isGa4 ? 'GA4' : (isEcommerce ? 'ECOMMERCE' : null);
        const isCustom = customIds.has(columnId);

        if (!flexRowsByMetric[metricName]) {
            // Comparativas sobre métricas personalizadas (metricName = "CUSTOM:<id>"): resolvemos el
            // display_name de la métrica (sino quedaba la clave cruda "CUSTOM:6") y las agrupamos
            // bajo "Métricas personalizadas".
            const isCustomMetricKey = typeof metricName === 'string' && metricName.startsWith('CUSTOM:');
            const cmMeta = isCustomMetricKey ? customMetricsByKeyAll[metricName] : null;
            let friendlyName;
            if (isCustomMetricKey) {
                friendlyName = (cmMeta && cmMeta.display_name) ? cmMeta.display_name : metricName;
            } else {
                // Pretty name vía getMetricName (i18n keys metrics.meta_video_view, etc).
                // Fallback al prettyMetricName con prefijos cuando no hay traducción.
                const translated = getMetricName(t, metricName);
                friendlyName = translated !== metricName ? translated : prettyMetricName(metricName);
            }
            flexRowsByMetric[metricName] = {
                id: `FLEX_${metricName}`,
                metricName,
                titleKey: friendlyName,
                iconName: isCustomMetricKey ? 'wand-magic-sparkles' : iconForFlex(lowerName),
                category: isCustomMetricKey ? CATEGORY_CUSTOM : category,
                channel: isCustomMetricKey ? CHANNEL_BU : channel,
                type: ROW_TYPE_FIXED,
                requiresIntegration: isCustomMetricKey ? null : requiresIntegration,
                isFlex: isCustom,  // badge ✨ sólo si TODOS los chips de la métrica son custom
                isCustomMetric: isCustomMetricKey || undefined,
                isAdvanced: isCustomMetricKey ? false : isAdvancedMetric(metricName),
                chips: [],
            };
        } else if (!isCustom) {
            // Si una chip de la misma métrica viene de fixed, ya no es 100% custom → quitamos el badge
            flexRowsByMetric[metricName].isFlex = false;
        }
        flexRowsByMetric[metricName].chips.push({label: compact.short, fullLabel: compact.full, columnId, channel});
    });

    Object.values(flexRowsByMetric).forEach((row) => rows.push(row));

    // -------- AGENCY CUSTOM METRICS (auto-derivados) --------
    // UI-4: indicators auto-derivados de cada AgencyCustomMetric activa × períodos canónicos.
    // El frontend los deriva client-side desde `customMetrics` (basic-data Fase 3) —
    // así no dependemos de un campo separado del backend. Si por compat el backend
    // los manda en `customMetricIndicators`, usamos esos; sino los generamos acá.
    const autoCustomMetricIndicators = (customMetricIndicators && customMetricIndicators.length > 0)
        ? customMetricIndicators
        : buildAutoCustomMetricIndicators(customMetrics, flexIndicators);
    if (Array.isArray(autoCustomMetricIndicators) && autoCustomMetricIndicators.length > 0) {
        // index custom metrics por metric_key para metadata extra (has_broken_reference, etc.)
        const customMetricsByKey = {};
        (customMetrics || []).forEach((cm) => {
            if (!cm || cm.id == null) return;
            customMetricsByKey['CUSTOM:' + cm.id] = cm;
        });
        const customRowsByMetric = {};
        autoCustomMetricIndicators.forEach((columnId) => {
            if (typeof columnId !== 'string' || !columnId.includes(AGENCY_FLEX_INDICATOR)) return;
            const parts = FLEX_INDICATOR_COLUMN_TO_DATA(columnId);
            if (!parts || !parts.metricName) return;
            const {metricName, mainPeriod, vsPeriod} = parts;
            const cmMeta = customMetricsByKey[metricName] || {};
            const compact = compactPeriodLabel(t, mainPeriod, vsPeriod);
            if (!customRowsByMetric[metricName]) {
                customRowsByMetric[metricName] = {
                    id: `CUSTOM_METRIC_${metricName}`,
                    metricName,
                    titleKey: cmMeta.display_name || metricName,
                    iconName: cmMeta.has_broken_reference ? 'exclamation-triangle' : 'cog',
                    category: CATEGORY_CUSTOM,
                    channel: CHANNEL_BU,
                    type: ROW_TYPE_FIXED,
                    isFlex: false,
                    isCustomMetric: true,
                    hasBrokenReference: !!cmMeta.has_broken_reference,
                    requiresPostAggregation: !!cmMeta.requires_post_aggregation,
                    higherIsBetter: cmMeta.higher_is_better !== false,
                    valueFormat: cmMeta.value_format || 'DECIMAL',
                    chips: [],
                };
            }
            customRowsByMetric[metricName].chips.push({
                label: compact.short, fullLabel: compact.full, columnId, channel: CHANNEL_BU,
            });
        });
        Object.values(customRowsByMetric).forEach((row) => rows.push(row));
    }

    return rows;
}

// Métricas "avanzadas" = se ocultan por default detrás de un toggle "Mostrar avanzadas".
// Lista negativa: cualquier cosa que NO sea core. Core = las métricas que la mayoría de las agencies usan
// día a día. Avanzadas = Meta_Like/Comment/Click_to_call, Google_Search_Lost_*, etc.
const CORE_METRIC_NAMES = new Set([
    'Cost', 'Clicks', 'Impressions', 'CPC', 'CTR', 'CPM', 'CPA', 'Conversions',
    'Purchase Value', 'ROAS', 'ACOS', 'Conversion Rate',
    'GA4_Sessions', 'GA4_Active Users', 'GA4_Conversions', 'GA4_Ecommerce Purchases',
    'GA4_Purchase Revenue', 'GA4_ROAS', 'GA4_ACOS', 'GA4_CPA',
    'Ecommerce_Sessions', 'Ecommerce_Gross Revenue', 'Ecommerce_Orders',
    'Ecommerce_Avg Gross Order Value', 'Ecommerce_ROAS', 'Ecommerce_ACOS',
    'Ecommerce_Conversion Rate', 'Ecommerce_CPA',
    'Reach (Meta)', 'Frequency (Meta)',
]);

function isAdvancedMetric(metricName) {
    return !CORE_METRIC_NAMES.has(metricName);
}

function prettyMetricName(metricName) {
    // 'Cost' → 'Costo'; 'GA4_ROAS' → 'ROAS (GA4)'; 'Ecommerce_Orders' → 'Orders (Ecommerce)'
    if (!metricName) return '';
    if (metricName.startsWith('GA4_')) return `${metricName.slice(4)} (GA4)`;
    if (metricName.startsWith('Ecommerce_')) return `${metricName.slice(10)} (Ecommerce)`;
    return metricName;
}

function iconForFlex(lowerName) {
    if (lowerName.includes('roas')) return 'chart-bar';
    if (lowerName.includes('acos')) return 'chart-pie';
    if (lowerName.includes('cpa')) return 'bullseye';
    if (lowerName.includes('cost')) return 'dollar-sign';
    if (lowerName.includes('click')) return 'mouse-pointer';
    if (lowerName.includes('session')) return 'globe';
    if (lowerName.includes('purchase') || lowerName.includes('revenue')) return 'shopping-cart';
    if (lowerName.includes('order')) return 'shopping-bag';
    if (lowerName.includes('conversion')) return 'check-double';
    return 'chart-line';
}

// Devuelve los IDs de columnas activas que pertenecen a alguna chip de la fila.
export function activeColumnIdsInRow(row, activeColumns) {
    const activeSet = new Set(activeColumns);
    return row.chips.filter((chip) => activeSet.has(chip.columnId)).map((chip) => chip.columnId);
}

// Cuenta cuántos chips de la fila están activos
export function countActiveChips(row, activeColumns) {
    return activeColumnIdsInRow(row, activeColumns).length;
}
